// Matches the server's response grant and recovery cookie lifetime.
const RESPONSE_GRANT_LIFETIME_MS = 24 * 60 * 60 * 1000;

/** Read an ID written by a public enrollment while its original grant is still eligible. */
function enrolledResponseId(area: 'sessionStorage' | 'localStorage', roomId: string): string | null {
  try {
    const item = window[area].getItem(`holito_public_did_${roomId}`);
    if (!item) return null;
    const pointer = JSON.parse(item);
    if (typeof pointer?.id === 'string' && Number.isFinite(pointer.expiresAt) && pointer.expiresAt > Date.now()) return pointer.id;
  } catch { /* Storage may be blocked independently in each area. */ }
  return null;
}

/** Older history entries can still redeem their tab pointer. New joins only use IDs from public enrollments. */
export function publicResponseId(roomId: string, allowLegacyTab: boolean): string | null {
  if (allowLegacyTab) {
    try {
      const id = sessionStorage.getItem(`holito_did_${roomId}`);
      if (id) return id;
    } catch { /* Cross-tab recovery can still work. */ }
  }
  return enrolledResponseId('localStorage', roomId) ?? enrolledResponseId('sessionStorage', roomId);
}

/** Keep the original tab locator for old clients. Share only IDs issued for a new public enrollment. */
export function rememberPublicResponse(roomId: string, responseId: string, newlyEnrolled: boolean): void {
  try { sessionStorage.setItem(`holito_did_${roomId}`, responseId); } catch { /* Storage is optional. */ }
  if (!newlyEnrolled) return;
  const pointer = JSON.stringify({ id: responseId, expiresAt: Date.now() + RESPONSE_GRANT_LIFETIME_MS });
  for (const area of ['sessionStorage', 'localStorage'] as const) {
    try { window[area].setItem(`holito_public_did_${roomId}`, pointer); } catch { /* Use whichever area is available. */ }
  }
}

/** Forget this response without clearing a newer response selected in another tab. */
export function forgetResponse(roomId: string, responseId: string | null): void {
  if (!responseId) return;
  const legacyKey = `holito_did_${roomId}`;
  try {
    if (sessionStorage.getItem(legacyKey) === responseId) sessionStorage.removeItem(legacyKey);
  } catch { /* Storage is optional. */ }
  for (const area of ['sessionStorage', 'localStorage'] as const) {
    if (enrolledResponseId(area, roomId) === responseId) {
      try { window[area].removeItem(`holito_public_did_${roomId}`); } catch { /* Storage is optional. */ }
    }
  }
}
